import { z } from 'zod';
import { uuidSchema } from './common.js';
/**
 * Contract Template Library (BP-24, D-148/D-151). Platform seeds + org-owned versioned templates
 * (published versions immutable, BP-22 mechanics org-scoped), allocations to RfX/supplier/project,
 * and a token-filled render. Members draft + allocate; org_admin publishes/archives (D-151 OQ-1).
 * Generation/render is gated on the template-disclaimer acknowledgement (OQ-2/OQ-3).
 */
export const templateDocTypeSchema = z.enum(['nda', 'msa', 'project', 'sow', 'custom']);
export const templateStatusSchema = z.enum(['draft', 'published', 'archived']);
export const allocationTargetTypeSchema = z.enum(['rfx', 'supplier', 'project']);
export const contractTemplateSchema = z.object({
    id: uuidSchema,
    familyId: uuidSchema,
    orgOwned: z.boolean(), // false = platform seed (read-only)
    docType: templateDocTypeSchema,
    name: z.string(),
    version: z.number().int(),
    status: templateStatusSchema,
    bodyMd: z.string(),
    tokens: z.array(z.string()),
    sha256: z.string().nullable(),
    isDemo: z.boolean(),
    publishedAt: z.string().nullable(),
    updatedAt: z.string(),
});
/** POST /contract-templates/drafts — copy-on-write from a seed or an org version (any member). */
export const createTemplateDraftSchema = z
    .object({
    sourceId: uuidSchema, // the template version to copy from
    name: z.string().trim().min(1).max(200).optional(),
})
    .strict();
/** PATCH /contract-templates/drafts/:id — edit a draft (any member; published versions are immutable). */
export const patchTemplateDraftSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    bodyMd: z.string().min(1).max(200_000).optional(),
})
    .strict();
export const templateAllocationSchema = z.object({
    id: uuidSchema,
    familyId: uuidSchema,
    templateVersion: z.number().int(),
    targetType: allocationTargetTypeSchema,
    targetId: uuidSchema.nullable(),
    targetLabel: z.string(),
    note: z.string().nullable(),
    isDemo: z.boolean(),
    createdAt: z.string(),
});
/** POST /contract-templates/allocations — allocate a template version (any member; audited). */
export const createTemplateAllocationSchema = z
    .object({
    familyId: uuidSchema,
    templateVersion: z.number().int().min(1),
    targetType: allocationTargetTypeSchema,
    targetId: uuidSchema.optional(), // required for supplier targets (label resolves server-side)
    targetLabel: z.string().trim().min(1).max(200).optional(), // required for rfx/project targets
    note: z.string().trim().max(500).optional(),
})
    .strict();
/** GET /contract-templates/:id/render?supplierId= — tokens filled from live records, nothing invented. */
export const templateRenderSchema = z.object({
    name: z.string(),
    version: z.number().int(),
    bodyMd: z.string(),
    filled: z.array(z.string()),
    unfilled: z.array(z.string()),
});
/** GET /contract-templates/disclaimer — the gate document + the caller's acceptance state. */
export const templateDisclaimerSchema = z.object({
    version: z.string(),
    title: z.string(),
    bodyMd: z.string(),
    accepted: z.boolean(),
});
//# sourceMappingURL=templates.js.map